/*
 * Tona · PR-18 · Mega Menu (blocks/_tona-mega-menu.liquid)
 *
 * Abre el desplegable de cada link de arriba:
 *  - mouse: con demora de intención al abrir o cambiar (data-abrir) y al cerrar (data-cerrar), sin parpadeo;
 *  - teclado: el foco en un link lo abre, Tab entra, Esc cierra y devuelve el foco al link, flecha abajo entra;
 *  - toque: el primer toque abre, el segundo sigue el link;
 *  - editor de temas: shopify:block:select abre el desplegable del bloque elegido y lo deja abierto.
 *
 * El vidrio lo pinta la barra del header: acá solo se le avisa cuánto mide el desplegable abierto
 * (--tona-mm-alto en #header-component) y que hay uno abierto (data-tona-mm-abierto).
 *
 * PR-31 (2026-09-17) · abajo del corte el mismo menú es el MENÚ DE CELULAR: tona-menu-movil (al final
 * de este archivo) hace bajar el panel desde el header y acá adentro cambia lo que depende del modo:
 *  - el mouse no abre nada (en celular no hay mouse, y en una compu angosta abrir al pasar molesta);
 *  - el texto lleva a la página y la flechita de al lado abre la sección adentro (el acordeón);
 *  - el alto del vidrio lo mide tona-menu-movil, así que medir() no escribe --tona-mm-alto.
 * Los dos elementos NO comparten estado: cuando el acordeón cambia de alto, tona-menu-movil se
 * entera con un ResizeObserver sobre la lista, no con un aviso.
 *
 * Sin template literals ni llaves pegadas: Shopify descarta en silencio archivos con llaves pegadas a un texto al subirlos por URL.
 */
const ITEM = '[data-tona-mm-item]';
const FILA = '.tona-mm__contenido';
const TOGGLE = '[data-tona-mm-toggle]';

class TonaMegaMenu extends HTMLElement {
  connectedCallback() {
    if (this.conectado) return;
    this.conectado = true;
    this.header = this.closest('header-component') || document.getElementById('header-component');
    this.demoraAbrir = parseInt(this.dataset.abrir || '80', 10);
    this.demoraCerrar = parseInt(this.dataset.cerrar || '240', 10);
    this.abierto = null;
    this.fijado = null;
    this.tAbrir = 0;
    this.tCerrar = 0;
    this.tOcultar = 0;
    this.ultimoToque = 0;

    this.alEntrar = this.alEntrar.bind(this);
    this.alSalir = this.alSalir.bind(this);
    this.alFoco = this.alFoco.bind(this);
    this.alPerderFoco = this.alPerderFoco.bind(this);
    this.alTecla = this.alTecla.bind(this);
    this.alClic = this.alClic.bind(this);
    this.alTocar = this.alTocar.bind(this);
    this.alTocarAfuera = this.alTocarAfuera.bind(this);
    this.alTeclaDocumento = this.alTeclaDocumento.bind(this);
    this.alElegirBloque = this.alElegirBloque.bind(this);
    this.alSoltarBloque = this.alSoltarBloque.bind(this);
    this.alRedimensionar = this.alRedimensionar.bind(this);
    this.alRueda = this.alRueda.bind(this);
    this.alBajarPuntero = this.alBajarPuntero.bind(this);
    this.alMoverPuntero = this.alMoverPuntero.bind(this);
    this.alSoltarPuntero = this.alSoltarPuntero.bind(this);
    this.alClicFila = this.alClicFila.bind(this);
    this.alCorrer = this.alCorrer.bind(this);
    this.alFlecha = this.alFlecha.bind(this);
    this.alCargarSeccion = this.alCargarSeccion.bind(this);
    this.alToggle = this.alToggle.bind(this);

    this.medidor = new ResizeObserver(() => this.medir());
    this.medidorFilas = new ResizeObserver((entradas) => {
      entradas.forEach((e) => this.actualizarFlechas(e.target));
    });

    this.items().forEach((li) => {
      // Marca en una propiedad y no en un atributo: un atributo viaja en el HTML si el editor copia la sección
      if (li.tonaMmListo) return;
      li.tonaMmListo = true;
      li.addEventListener('pointerenter', this.alEntrar);
      li.addEventListener('pointerleave', this.alSalir);
      li.addEventListener('focusin', this.alFoco);
      li.addEventListener('focusout', this.alPerderFoco);
      li.addEventListener('keydown', this.alTecla);
      const link = this.linkDe(li);
      if (link) {
        link.addEventListener('click', this.alClic);
        link.addEventListener('pointerdown', this.alTocar);
      }
      const toggle = this.toggleDe(li);
      if (toggle) toggle.addEventListener('click', this.alToggle);
    });

    this.engancharFilas();

    document.addEventListener('pointerdown', this.alTocarAfuera);
    document.addEventListener('keydown', this.alTeclaDocumento);
    document.addEventListener('shopify:block:select', this.alElegirBloque);
    document.addEventListener('shopify:block:deselect', this.alSoltarBloque);
    document.addEventListener('shopify:section:load', this.alCargarSeccion);
    window.addEventListener('resize', this.alRedimensionar);
  }

  disconnectedCallback() {
    this.conectado = false;
    document.removeEventListener('pointerdown', this.alTocarAfuera);
    document.removeEventListener('keydown', this.alTeclaDocumento);
    document.removeEventListener('shopify:block:select', this.alElegirBloque);
    document.removeEventListener('shopify:block:deselect', this.alSoltarBloque);
    document.removeEventListener('shopify:section:load', this.alCargarSeccion);
    window.removeEventListener('resize', this.alRedimensionar);
    this.medidor.disconnect();
    this.medidorFilas.disconnect();
    clearTimeout(this.tAbrir);
    clearTimeout(this.tCerrar);
    clearTimeout(this.tOcultar);
    if (this.header) {
      this.header.removeAttribute('data-tona-mm-abierto');
      this.header.style.setProperty('--tona-mm-alto', '0px');
    }
  }

  items() {
    return Array.from(this.querySelectorAll(ITEM)).filter((li) => li.closest('tona-mega-menu') === this);
  }

  linkDe(li) {
    return li.querySelector(':scope > .tona-mm__link');
  }

  panelDe(li) {
    return li.querySelector(':scope > .tona-mm__panel');
  }

  toggleDe(li) {
    return li.querySelector(':scope > ' + TOGGLE);
  }

  // PR-31 · lo pone y lo saca tona-menu-movil según el ancho y el ajuste «Menú de celular»
  enCelular() {
    return !!(this.header && this.header.hasAttribute('data-tona-menu-movil'));
  }

  marcarAbierto(li, abierto) {
    const link = this.linkDe(li);
    const toggle = this.toggleDe(li);
    if (link && link.hasAttribute('aria-expanded')) link.setAttribute('aria-expanded', abierto ? 'true' : 'false');
    if (toggle) toggle.setAttribute('aria-expanded', abierto ? 'true' : 'false');
  }

  tieneContenido(li) {
    const panel = this.panelDe(li);
    if (!panel) return false;
    if (window.Shopify && window.Shopify.designMode) return true;
    const contenido = panel.querySelector('.tona-mm__contenido');
    return !!(contenido && contenido.children.length);
  }

  visible() {
    const contenedor = this.closest('.tona-hdr__menu') || this;
    return getComputedStyle(contenedor).display !== 'none';
  }

  // ── Abrir y cerrar ───────────────────────────────────────────────────
  abrir(li) {
    clearTimeout(this.tAbrir);
    clearTimeout(this.tCerrar);
    if (!li || !this.tieneContenido(li)) {
      if (this.abierto && this.abierto !== li) this.cerrar(true);
      return;
    }
    if (this.abierto === li) return;
    if (this.abierto) this.cerrar(true, true);

    const panel = this.panelDe(li);
    clearTimeout(this.tOcultar);
    panel.hidden = false;
    li.setAttribute('data-abierto', '');
    this.marcarAbierto(li, true);
    this.abierto = li;
    if (this.header) this.header.setAttribute('data-tona-mm-abierto', '');
    this.medir();
    const fila = panel.querySelector(FILA);
    if (fila) {
      fila.scrollLeft = 0;
      this.actualizarFlechas(fila);
    }
    this.medidor.observe(panel);
    requestAnimationFrame(() => {
      if (this.abierto === li) panel.setAttribute('data-visible', '');
    });
  }

  cerrar(ya, cambiando) {
    clearTimeout(this.tAbrir);
    clearTimeout(this.tCerrar);
    const li = this.abierto;
    if (!li) return;
    const panel = this.panelDe(li);
    this.abierto = null;
    li.removeAttribute('data-abierto');
    this.marcarAbierto(li, false);
    if (panel) {
      this.medidor.unobserve(panel);
      panel.removeAttribute('data-visible');
      if (ya) {
        panel.hidden = true;
      } else {
        this.tOcultar = setTimeout(() => {
          if (this.abierto !== li) panel.hidden = true;
        }, 170);
      }
    }
    if (!cambiando && this.header) {
      this.header.removeAttribute('data-tona-mm-abierto');
      this.header.style.setProperty('--tona-mm-alto', '0px');
    }
  }

  medir() {
    if (!this.header) return;
    // PR-31: en celular el desplegable va en el flujo, adentro del panel que baja del header, así que
    // no le suma alto al vidrio por su cuenta: lo mide tona-menu-movil de una sola vez.
    if (this.enCelular()) {
      this.header.style.setProperty('--tona-mm-alto', '0px');
      return;
    }
    const panel = this.abierto ? this.panelDe(this.abierto) : null;
    const alto = panel && !panel.hidden ? Math.round(panel.getBoundingClientRect().height) : 0;
    this.header.style.setProperty('--tona-mm-alto', alto + 'px');
  }

  // ── Correr la fila de costado (PR-29) ─────────────────────────────────
  // En compu no hay forma de recorrer una fila que se pasa de ancho: el dedo la corre en celular y
  // el mouse no. Se le agregan las tres formas que espera alguien con mouse: la rueda, arrastrar y
  // dos flechas. Las flechas se esconden en cada punta y desaparecen si la fila entra entera.
  filas() {
    return Array.from(this.querySelectorAll(FILA)).filter((f) => f.closest('tona-mega-menu') === this);
  }

  engancharFilas() {
    this.filas().forEach((fila) => {
      if (!fila.tonaMmFilaLista) {
        fila.tonaMmFilaLista = true;
        fila.addEventListener('wheel', this.alRueda, { passive: false });
        fila.addEventListener('pointerdown', this.alBajarPuntero);
        fila.addEventListener('pointermove', this.alMoverPuntero);
        fila.addEventListener('pointerup', this.alSoltarPuntero);
        fila.addEventListener('pointercancel', this.alSoltarPuntero);
        fila.addEventListener('click', this.alClicFila, true);
        fila.addEventListener('scroll', this.alCorrer, { passive: true });
        this.medidorFilas.observe(fila);
        const panel = fila.closest('.tona-mm__panel');
        if (panel) {
          panel.querySelectorAll('[data-tona-mm-flecha]').forEach((b) => b.addEventListener('click', this.alFlecha));
        }
      }
      this.actualizarFlechas(fila);
    });
  }

  sobra(fila) {
    return fila.scrollWidth - fila.clientWidth;
  }

  actualizarFlechas(fila) {
    if (!fila || !fila.closest) return;
    const panel = fila.closest('.tona-mm__panel');
    if (!panel) return;
    const sobra = this.sobra(fila);
    const corrible = sobra > 1;
    fila.classList.toggle('is-corrible', corrible);
    const izq = panel.querySelector('[data-tona-mm-flecha="-1"]');
    const der = panel.querySelector('[data-tona-mm-flecha="1"]');
    if (izq) izq.hidden = !corrible || fila.scrollLeft <= 1;
    if (der) der.hidden = !corrible || fila.scrollLeft >= sobra - 1;
  }

  alCorrer(evento) {
    this.actualizarFlechas(evento.currentTarget);
  }

  alFlecha(evento) {
    const boton = evento.currentTarget;
    const panel = boton.closest('.tona-mm__panel');
    const fila = panel && panel.querySelector(FILA);
    if (!fila) return;
    evento.preventDefault();
    const suave = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    const paso = Math.max(160, Math.round(fila.clientWidth * 0.8));
    fila.scrollBy({ left: paso * parseInt(boton.dataset.tonaMmFlecha, 10), behavior: suave });
  }

  alRueda(evento) {
    const fila = evento.currentTarget;
    if (evento.ctrlKey || this.sobra(fila) <= 1) return;
    const factor = evento.deltaMode === 1 ? 16 : evento.deltaMode === 2 ? fila.clientWidth : 1;
    const delta = (Math.abs(evento.deltaX) > Math.abs(evento.deltaY) ? evento.deltaX : evento.deltaY) * factor;
    if (!delta) return;
    const antes = fila.scrollLeft;
    fila.scrollLeft = antes + delta;
    // Solo se queda con el gesto si de verdad corrió: en las puntas la página sigue scrolleando
    if (fila.scrollLeft !== antes) evento.preventDefault();
  }

  alBajarPuntero(evento) {
    const fila = evento.currentTarget;
    if (evento.pointerType === 'touch' || evento.button !== 0 || this.sobra(fila) <= 1) return;
    if (evento.target.closest('[data-tona-mm-flecha]')) return;
    fila.tonaArrastre = { x: evento.clientX, scroll: fila.scrollLeft, movio: false, id: evento.pointerId };
  }

  alMoverPuntero(evento) {
    const fila = evento.currentTarget;
    const a = fila.tonaArrastre;
    if (!a || a.id !== evento.pointerId) return;
    const dx = evento.clientX - a.x;
    if (!a.movio) {
      if (Math.abs(dx) < 4) return;
      a.movio = true;
      fila.classList.add('is-arrastrando');
      if (fila.setPointerCapture) fila.setPointerCapture(evento.pointerId);
    }
    fila.scrollLeft = a.scroll - dx;
    evento.preventDefault();
  }

  alSoltarPuntero(evento) {
    const fila = evento.currentTarget;
    const a = fila.tonaArrastre;
    if (!a) return;
    fila.tonaArrastre = null;
    fila.classList.remove('is-arrastrando');
    if (!a.movio) return;
    if (fila.releasePointerCapture && fila.hasPointerCapture && fila.hasPointerCapture(evento.pointerId)) {
      fila.releasePointerCapture(evento.pointerId);
    }
    // El clic llega justo después de soltar: si se arrastró, no tiene que navegar
    fila.tonaSinClic = true;
    setTimeout(() => {
      fila.tonaSinClic = false;
    }, 0);
  }

  alClicFila(evento) {
    const fila = evento.currentTarget;
    if (!fila.tonaSinClic) return;
    evento.preventDefault();
    evento.stopPropagation();
  }

  alCargarSeccion(evento) {
    if (!this.isConnected) return;
    const objetivo = evento.target;
    if (objetivo instanceof Element && !objetivo.contains(this)) return;
    this.engancharFilas();
    this.medir();
  }

  programarCierre() {
    if (this.fijado) return;
    clearTimeout(this.tCerrar);
    this.tCerrar = setTimeout(() => this.cerrar(false), this.demoraCerrar);
  }

  // ── Mouse ─────────────────────────────────────────────────────────────
  alEntrar(evento) {
    if (evento.pointerType === 'touch' || this.fijado || this.enCelular()) return;
    const li = evento.currentTarget;
    clearTimeout(this.tCerrar);
    clearTimeout(this.tAbrir);
    if (this.abierto === li) return;
    this.tAbrir = setTimeout(() => this.abrir(li), this.demoraAbrir);
  }

  alSalir(evento) {
    if (evento.pointerType === 'touch' || this.enCelular()) return;
    clearTimeout(this.tAbrir);
    // Si el foco está adentro por teclado, se queda abierto; con el mouse se cierra siempre al salir
    if (this.abierto && !this.abierto.querySelector(':focus-visible')) this.programarCierre();
  }

  // ── Teclado ───────────────────────────────────────────────────────────
  alFoco(evento) {
    const li = evento.currentTarget;
    clearTimeout(this.tCerrar);
    // En celular el foco no abre: es un acordeón, y tabular por la lista no tiene que ir desplegando todo
    if (this.sinAbrirPorFoco || this.enCelular()) return;
    if (this.abierto !== li && Date.now() - this.ultimoToque > 600) this.abrir(li);
  }

  alPerderFoco(evento) {
    const li = evento.currentTarget;
    const destino = evento.relatedTarget;
    if (destino && li.contains(destino)) return;
    if (this.abierto === li && !li.matches(':hover') && !this.fijado) this.cerrar(false);
  }

  alTecla(evento) {
    const li = evento.currentTarget;
    const link = this.linkDe(li);
    if (evento.key === 'Escape' && this.abierto === li) {
      evento.preventDefault();
      evento.stopPropagation();
      this.cerrar(true);
      this.devolverFoco(link);
      return;
    }
    if (evento.key === 'ArrowDown' && evento.target === link && this.tieneContenido(li)) {
      evento.preventDefault();
      this.abrir(li);
      const primero = this.panelDe(li).querySelector('a[href], button, [tabindex]:not([tabindex="-1"])');
      if (primero) primero.focus();
    }
  }

  alTeclaDocumento(evento) {
    if (evento.key !== 'Escape' || !this.abierto) return;
    const link = this.linkDe(this.abierto);
    const volver = this.abierto.contains(document.activeElement);
    this.cerrar(true);
    if (volver) this.devolverFoco(link);
  }

  // Devuelve el foco al link de arriba sin que el foco lo vuelva a abrir
  devolverFoco(link) {
    if (!link) return;
    this.sinAbrirPorFoco = true;
    link.focus();
    this.sinAbrirPorFoco = false;
  }

  // ── Toque y clic ──────────────────────────────────────────────────────
  alTocar(evento) {
    if (evento.pointerType === 'touch' || evento.pointerType === 'pen') this.ultimoToque = Date.now();
  }

  // PR-31 · la flechita del costado, que es la que abre la sección en el menú de celular
  alToggle(evento) {
    evento.preventDefault();
    evento.stopPropagation();
    const li = evento.currentTarget.closest(ITEM);
    if (!li || !this.tieneContenido(li)) return;
    // cerrar(true) y no cerrar(false): con la demora de 170 ms el panel de celular mediría el alto
    // viejo y daría un salto al recalcularse
    if (this.abierto === li && !this.fijado) this.cerrar(true);
    else this.abrir(li);
  }

  alClic(evento) {
    const link = evento.currentTarget;
    const li = link.closest(ITEM);
    if (!li || !this.tieneContenido(li)) return;
    const esBoton = link.tagName === 'BUTTON';
    // En celular el texto es el link a la página; la sección la abre la flechita de al lado.
    // Un ítem sin link (un botón, como Archivo) sí abre con el texto: no tiene a dónde llevar.
    if (this.enCelular()) {
      if (!esBoton) return;
      evento.preventDefault();
      if (this.abierto === li && !this.fijado) this.cerrar(true);
      else this.abrir(li);
      return;
    }
    const porToque = Date.now() - this.ultimoToque < 800 || window.matchMedia('(hover: none)').matches;
    if (esBoton) {
      evento.preventDefault();
      if (this.abierto === li && !this.fijado) this.cerrar(false);
      else this.abrir(li);
      return;
    }
    if (porToque && this.abierto !== li) {
      evento.preventDefault();
      this.abrir(li);
    }
  }

  alTocarAfuera(evento) {
    if (!this.abierto || this.fijado) return;
    if (this.contains(evento.target)) return;
    // En celular la sección abierta la cierra su flechita: tocar la hamburguesa o el velo cierra el
    // menú entero, y si el acordeón se cerrara además por su cuenta el panel daría un salto de alto
    if (this.enCelular()) return;
    this.cerrar(false);
  }

  alRedimensionar() {
    if (this.abierto && !this.visible()) this.cerrar(true);
    else if (this.abierto) this.medir();
    this.filas().forEach((fila) => this.actualizarFlechas(fila));
  }

  // ── Editor de temas ───────────────────────────────────────────────────
  alElegirBloque(evento) {
    const objetivo = evento.target;
    if (!(objetivo instanceof Element) || !this.contains(objetivo)) return;
    const li = objetivo.closest(ITEM);
    if (!li) return;
    this.fijado = li;
    this.abrir(li);
  }

  alSoltarBloque(evento) {
    const objetivo = evento.target;
    if (!(objetivo instanceof Element) || !this.contains(objetivo)) return;
    this.fijado = null;
    this.cerrar(false);
  }
}

/*
 * PR-31 · TONA-MENU-MOVIL · el marco del menú de celular.
 *
 * Envuelve al mega menú (es el mismo .tona-hdr__menu que en compu va en el renglón del header) y
 * abajo del corte lo hace bajar desde la barra, con el mismo vidrio, sin tapar toda la pantalla:
 *  - el alto es lo que mida el contenido, con tope en el % de pantalla del ajuste;
 *  - lo que queda debajo se ve oscurecido (el velo) y cierra al tocarlo;
 *  - cierra también con la X, con Esc y con la hamburguesa, y el foco vuelve a la hamburguesa;
 *  - si el contenido no entra en el tope, se corre adentro y la página de atrás queda trabada.
 */
class TonaMenuMovil extends HTMLElement {
  connectedCallback() {
    if (this.conectado) return;
    this.conectado = true;
    this.header = this.closest('header-component') || document.getElementById('header-component');
    if (!this.header) return;
    this.corte = parseInt(this.dataset.corte || '990', 10);
    this.tope = parseInt(this.dataset.tope || '85', 10);
    this.estaAbierto = false;

    this.alBoton = this.alBoton.bind(this);
    this.alVelo = this.alVelo.bind(this);
    this.alTeclaDocumento = this.alTeclaDocumento.bind(this);
    this.alRedimensionar = this.alRedimensionar.bind(this);
    this.alCargarSeccion = this.alCargarSeccion.bind(this);
    this.alElegirBloque = this.alElegirBloque.bind(this);

    // Mide la LISTA y no el marco: el marco está animando su alto, así que observarlo se muerde la cola
    this.medidor = new ResizeObserver(() => {
      if (this.estaAbierto) this.medir();
    });

    this.engancharBotones();
    document.addEventListener('keydown', this.alTeclaDocumento);
    document.addEventListener('shopify:section:load', this.alCargarSeccion);
    document.addEventListener('shopify:block:select', this.alElegirBloque);
    window.addEventListener('resize', this.alRedimensionar);
    this.sincronizarModo();
  }

  disconnectedCallback() {
    this.conectado = false;
    this.cerrar(true);
    document.removeEventListener('keydown', this.alTeclaDocumento);
    document.removeEventListener('shopify:section:load', this.alCargarSeccion);
    document.removeEventListener('shopify:block:select', this.alElegirBloque);
    window.removeEventListener('resize', this.alRedimensionar);
    this.medidor.disconnect();
    if (this.header) this.header.removeAttribute('data-tona-menu-movil');
  }

  engancharBotones() {
    this.boton = this.header.querySelector('[data-tona-menu-boton]');
    this.velo = this.header.querySelector('[data-tona-menu-velo]');
    if (this.boton && !this.boton.tonaMenuListo) {
      this.boton.tonaMenuListo = true;
      this.boton.addEventListener('click', this.alBoton);
    }
    if (this.velo && !this.velo.tonaMenuListo) {
      this.velo.tonaMenuListo = true;
      this.velo.addEventListener('click', this.alVelo);
    }
  }

  lista() {
    return this.querySelector('.tona-mm__principal');
  }

  megaMenu() {
    return this.querySelector('tona-mega-menu');
  }

  enCelular() {
    return this.header.dataset.tonaMenu === 'vidrio' && window.innerWidth < this.corte;
  }

  sincronizarModo() {
    const cel = this.enCelular();
    if (cel) {
      this.header.setAttribute('data-tona-menu-movil', '');
      this.header.style.setProperty('--tona-mm-alto', '0px');
    } else if (this.header.hasAttribute('data-tona-menu-movil')) {
      this.header.removeAttribute('data-tona-menu-movil');
      this.cerrar(true);
      // La sección que quedó abierta como acordeón no tiene que aparecer abierta como desplegable de compu
      const mm = this.megaMenu();
      if (mm && typeof mm.cerrar === 'function') mm.cerrar(true);
    }
  }

  // ── Medir ─────────────────────────────────────────────────────────────
  // Dos números: hasta dónde puede llegar (el tope) y cuánto mide de verdad el contenido.
  // El panel arranca donde termina la barra, así que el tope se cuenta desde ahí y no desde 0.
  medir() {
    const lista = this.lista();
    if (!lista) return 0;
    const arriba = this.getBoundingClientRect().top;
    const tope = Math.max(120, Math.round(window.innerHeight * (this.tope / 100) - arriba));
    const contenido = Math.ceil(lista.getBoundingClientRect().height);
    const alto = Math.min(contenido, tope);
    this.header.style.setProperty('--tona-menu-alto', alto + 'px');
    return alto;
  }

  // ── Abrir y cerrar ────────────────────────────────────────────────────
  abrir() {
    if (this.estaAbierto || !this.enCelular()) return;
    this.estaAbierto = true;
    clearTimeout(this.tVelo);
    if (this.velo) {
      this.velo.hidden = false;
      // Un reflow antes de prender el atributo: sin esto el velo pasa de display none a opacidad 1
      // de una y no hay transición que valga
      void this.velo.offsetHeight;
    }
    this.medir();
    this.header.setAttribute('data-tona-menu-abierto', '');
    document.documentElement.setAttribute('scroll-lock', '');
    if (this.boton) this.boton.setAttribute('aria-expanded', 'true');
    const lista = this.lista();
    if (lista) this.medidor.observe(lista);
    this.abrirElDeEntrada();
  }

  // Como en EME: el desplegable marcado en el editor ya aparece abierto al abrir el menú. Se aplica cada
  // vez que se abre, así que si la persona abrió otro y cerró el menú, al volver arranca de nuevo igual.
  abrirElDeEntrada() {
    const mm = this.megaMenu();
    if (!mm || typeof mm.abrir !== 'function') return;
    const li = this.querySelector('[data-tona-mm-abierto-celular]');
    if (!li) return;
    requestAnimationFrame(() => {
      if (!this.estaAbierto) return;
      mm.abrir(li);
      this.medir();
    });
  }

  cerrar(ya) {
    if (!this.estaAbierto) return;
    this.estaAbierto = false;
    this.header.removeAttribute('data-tona-menu-abierto');
    this.header.style.setProperty('--tona-menu-alto', '0px');
    document.documentElement.removeAttribute('scroll-lock');
    if (this.boton) this.boton.setAttribute('aria-expanded', 'false');
    this.medidor.disconnect();
    clearTimeout(this.tVelo);
    if (!this.velo) return;
    if (ya) {
      this.velo.hidden = true;
    } else {
      this.tVelo = setTimeout(() => {
        if (!this.estaAbierto && this.velo) this.velo.hidden = true;
      }, 240);
    }
  }

  alternar() {
    if (this.estaAbierto) this.cerrar(false);
    else this.abrir();
  }

  devolverFoco() {
    if (this.boton) this.boton.focus();
  }

  // ── Los gestos ────────────────────────────────────────────────────────
  alBoton(evento) {
    evento.preventDefault();
    this.alternar();
  }

  alVelo() {
    this.cerrar(false);
    this.devolverFoco();
  }

  alTeclaDocumento(evento) {
    if (evento.key !== 'Escape' || !this.estaAbierto) return;
    this.cerrar(false);
    this.devolverFoco();
  }

  alRedimensionar() {
    this.sincronizarModo();
    if (this.estaAbierto) this.medir();
  }

  // ── Editor de temas ───────────────────────────────────────────────────
  alCargarSeccion(evento) {
    if (!this.isConnected) return;
    const objetivo = evento.target;
    if (objetivo instanceof Element && !objetivo.contains(this)) return;
    this.engancharBotones();
    this.sincronizarModo();
    if (this.estaAbierto) this.medir();
  }

  // Elegir un bloque del menú en la barra lateral abre el panel, así se ve lo que se está editando
  alElegirBloque(evento) {
    const objetivo = evento.target;
    if (!(objetivo instanceof Element) || !this.contains(objetivo)) return;
    this.abrir();
    if (this.estaAbierto) requestAnimationFrame(() => this.medir());
  }
}

if (!customElements.get('tona-mega-menu')) {
  customElements.define('tona-mega-menu', TonaMegaMenu);
}

if (!customElements.get('tona-menu-movil')) {
  customElements.define('tona-menu-movil', TonaMenuMovil);
}
